import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { money, moneyCompact, type ColorScale, type TrendRow } from './analytics';

export const RevenueTrendChart: React.FC<{
  rows: TrendRow[];
  tiers: string[];
  color: ColorScale;
  isLoading?: boolean;
}> = ({ rows, tiers, color, isLoading }) => {
  const hasData = rows.some((r) => r.total > 0);
  const tickInterval = rows.length > 14 ? Math.ceil(rows.length / 10) : 0;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-gray-900">Promotion revenue</h3>
        <span className="text-xs text-gray-400">by tier</span>
      </div>

      <div className="relative">
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                interval={tickInterval}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
              />
              <YAxis
                width={54}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickFormatter={(v: number) => moneyCompact(v)}
              />
              <RechartsTooltip
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                formatter={(v: number, name: string) => [money(v), name]}
              />
              {tiers.map((tier, i) => (
                <Bar
                  key={tier}
                  dataKey={tier}
                  stackId="rev"
                  fill={color(tier)}
                  maxBarSize={44}
                  radius={i === tiers.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
          {tiers.map((tier) => (
            <span key={tier} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color(tier) }} />
              {tier}
            </span>
          ))}
        </div>

        {!hasData && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-400">
              {isLoading ? 'Loading revenue…' : 'No revenue in this period'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
